// Supported channel (types) for LCM studio
export const LcmStudioChannels = {
  Email: "email",
  Push: "push",
  Sms: "sms",
} as const;

export type LcmStudioChannel =
  (typeof LcmStudioChannels)[keyof typeof LcmStudioChannels];

/**
 * Channels delivered as a message body rather than an email. These share a
 * template/trigger-sync shape that the email path doesn't follow.
 */
export type LcmStudioMessageChannel =
  | typeof LcmStudioChannels.Push
  | typeof LcmStudioChannels.Sms;

const VALID_CHANNELS: readonly LcmStudioChannel[] =
  Object.values(LcmStudioChannels);

export function isLcmStudioChannel(value: string): value is LcmStudioChannel {
  return (VALID_CHANNELS as readonly string[]).includes(value);
}

// How email candidates should use their selected template.
//   - `adhere`: reskin the template — update copy/imagery, keep its structure.
//   - `deviate`: stay on-brand (colors, fonts, button styles, voice) but
//     restructure the layout into something visually distinct from the template.
// Chosen once at project creation and applied to every email candidate; stored
// per-plan (on the `plan` JSONB) so per-plan editing can override it later.
export const TemplateBehaviors = {
  Adhere: "adhere",
  Deviate: "deviate",
} as const;

export type TemplateBehavior =
  (typeof TemplateBehaviors)[keyof typeof TemplateBehaviors];

const VALID_TEMPLATE_BEHAVIORS: readonly TemplateBehavior[] =
  Object.values(TemplateBehaviors);

export function isTemplateBehavior(value: string): value is TemplateBehavior {
  return VALID_TEMPLATE_BEHAVIORS.includes(value as TemplateBehavior);
}

// How the selected email templates map onto a message's candidates. Chosen once
// at project creation, alongside the per-message candidate count. Drives both
// how many email candidates a message gets and how templates are assigned:
//   - PerTemplate  → N candidates from EACH template (total = N × #templates)
//   - AgentChoice  → N candidates total; the agent picks which template inspires
//                    each one (also covers "spread evenly across templates")
//   - Other        → N candidates total, following a free-form plan the user
//                    writes (carried in templateGuidance)
// Defaults to `per_template` — the historical "one candidate per template"
// behavior generalized to N — when unset. Only meaningful for email with
// templates; channels without templates ignore it.
export const TemplateStrategies = {
  PerTemplate: "per_template",
  AgentChoice: "agent_choice",
  Other: "other",
} as const;

export type TemplateStrategy =
  (typeof TemplateStrategies)[keyof typeof TemplateStrategies];

const VALID_TEMPLATE_STRATEGIES: readonly TemplateStrategy[] =
  Object.values(TemplateStrategies);

export function isTemplateStrategy(value: string): value is TemplateStrategy {
  return VALID_TEMPLATE_STRATEGIES.includes(value as TemplateStrategy);
}

// Bounds + defaults for the per-message candidate count (the "candidates per
// message" control). Shared here so the create form, the
// createProjectCampaign resolver clamp, and the message-plan skill all agree on
// one set of numbers instead of duplicating literals.
export const MIN_CANDIDATES_PER_MESSAGE = 1;
export const MAX_CANDIDATES_PER_MESSAGE = 10;
export const DEFAULT_CANDIDATES_PER_MESSAGE = 4;

/**
 * Clamp a user-supplied "candidates per message" value into the supported range,
 * rounding to an integer. Returns `undefined` when no number was provided, so
 * callers leave the skill's per-channel default in place. Shared by the create
 * and add-messages resolvers so a stray value can't fan out generation.
 */
export function clampCandidatesPerMessage(
  n: number | null | undefined,
): number | undefined {
  return typeof n === "number"
    ? Math.min(
        Math.max(Math.round(n), MIN_CANDIDATES_PER_MESSAGE),
        MAX_CANDIDATES_PER_MESSAGE,
      )
    : undefined;
}

/**
 * Resolve how many email candidates a message should get, from the chosen count
 * + template strategy + number of templates. An explicit count is honored
 * exactly (already clamped 1..10); RECOMMENDED_MIN is only a default for when no
 * count was chosen, never a floor on an explicit count.
 *   - no templates → the chosen count (recommended default when unset).
 *   - per_template → count from EACH template (count × #templates); one-per-
 *     template default when unset.
 *   - agent_choice / other → count total; #templates-or-recommended default when
 *     unset.
 * Shared by the message-plan skill (sizing a new plan) and the
 * generate-more-candidates resolver (sizing an append batch).
 */
export function resolveEmailCandidateCount({
  explicitCount,
  strategy,
  templateCount,
}: {
  explicitCount: number | undefined;
  strategy: TemplateStrategy;
  templateCount: number;
}): number {
  if (templateCount === 0) {
    return explicitCount ?? RECOMMENDED_MIN_CANDIDATES_PER_MESSAGE;
  }
  if (strategy === TemplateStrategies.PerTemplate) {
    return (explicitCount ?? 1) * templateCount;
  }
  return (
    explicitCount ??
    Math.max(templateCount, RECOMMENDED_MIN_CANDIDATES_PER_MESSAGE)
  );
}
// Floor applied when a channel/case has no templates to multiply (push/SMS, or
// email with no templates selected), and the minimum we recommend in the UI.
export const RECOMMENDED_MIN_CANDIDATES_PER_MESSAGE = 3;

// Whether a campaign targets one audience or is divided into multiple
// sub-audiences. Chosen once at project creation; drives how many audience
// segments the brief defines (the primary lever) and whether message plans
// target distinct sub-audiences. Defaults to `subsegments` — the historical
// behavior, where the brief always defined 2–5 segments — when unset.
// (Distinct from `ConceptInputData.audienceStrategy`, an unrelated
// concept-generation field below.)
export const AudienceScopes = {
  Subsegments: "subsegments",
  Single: "single",
} as const;

export type AudienceScope =
  (typeof AudienceScopes)[keyof typeof AudienceScopes];

const VALID_AUDIENCE_SCOPES: readonly AudienceScope[] =
  Object.values(AudienceScopes);

export function isAudienceScope(value: string): value is AudienceScope {
  return VALID_AUDIENCE_SCOPES.includes(value as AudienceScope);
}

// Wire format for the personalization toggle chosen at project creation, as
// sent by the create-project form (`CreateProjectCampaignInput`): a mode
// string plus optional free-text guidance. `resolvePersonalizationPolicy`
// normalizes the pair into the structured `PersonalizationPolicy` the agent
// contexts carry.
export const PersonalizationModes = {
  Allowed: "allowed",
  None: "none",
} as const;

export type PersonalizationMode =
  (typeof PersonalizationModes)[keyof typeof PersonalizationModes];

// Whether the agents may use personalization variables (dynamic per-recipient
// fields) in a campaign's brief, plans, and messages. A discriminated union so
// the invalid wire combination ("none" with guidance) is unrepresentable.
//   - `allowed`: the historical default — the brief picks tokens from the
//     schema, steered by the user's free-text `guidance` when provided
//     (e.g. "only personalize greetings with first name").
//   - `none`: every recipient receives identical content; the brief states
//     this explicitly so downstream agents carry the policy forward.
// The policy travels as brief/plan text (not stamped onto plan blobs), so it
// stays editable through conversation after creation.
export type PersonalizationPolicy =
  | { type: "allowed"; guidance?: string }
  | { type: "none" };

/**
 * Normalizes the wire-format personalization inputs into a
 * `PersonalizationPolicy`. Mirrors the other project-creation toggles: an
 * unknown mode falls back to the default (`allowed`), and guidance is
 * trimmed, dropped when blank, and ignored when personalization is off.
 */
export function resolvePersonalizationPolicy(
  mode: string | null | undefined,
  guidance: string | null | undefined,
): PersonalizationPolicy {
  if (mode === PersonalizationModes.None) {
    return { type: "none" };
  }
  const cleaned = guidance?.trim();
  return cleaned ? { type: "allowed", guidance: cleaned } : { type: "allowed" };
}

/**
 * The campaign's generation knobs — a single shared shape, all-optional. Used as
 * the per-run override carried on `InteractionContext.lcmConfigOverride` (the
 * "Add messages" modal sets the fields for one batch), and as the resolved bag
 * the message-plan skill builds by layering that override over the campaign's
 * session context (`lcmConfigOverride?.X ?? context.X ?? skill default`).
 *
 * An unset field means "use the skill's default" (see
 * MIN/DEFAULT_CANDIDATES_PER_MESSAGE, TemplateStrategies, etc.). Holds ONLY the
 * generation knobs — `autoMode` and `brandProfileId` are their own concerns, and
 * identity/infra keys (workspaceId, parentModelId, marketingCampaignId, dirs)
 * are not config.
 */
export interface LcmCampaignConfig {
  candidatesPerMessage?: number;
  templateStrategy?: TemplateStrategy;
  templateBehavior?: TemplateBehavior;
  templateGuidance?: string;
  candidateSplitPlan?: string;
  templateIds?: string[];
  componentIds?: string[];
  channels?: LcmStudioChannel[];
  audienceScope?: AudienceScope;
  personalization?: PersonalizationPolicy;
}

// Lifecycle status for a message plan (`lcm_message_plans.status`). The two
// transient states encode their owner class in the value itself, so each
// subsystem's guarded swaps can never land on the other's claim:
//   - `generating`: the initial candidate-generation queue job. The enqueuer
//     swaps draft/failed→generating; the job flips generating→accepted on
//     success and its terminal failure lands on `failed`. SQS redelivery is
//     the retry mechanism while the status is `generating`.
//   - `generating_more`: a candidate batch appended to an accepted plan
//     (accepted→generating_more for the batch's duration, restored to
//     accepted when it ends however it ends). The plan stays semantically
//     accepted throughout — its candidates remain immutable to the agent.
// `failed` stays claimable so a retry recovers the plan. All guarded
// transitions live in the message-plans service.
export const LcmMessagePlanStatuses = {
  Draft: "draft",
  Generating: "generating",
  GeneratingMore: "generating_more",
  Accepted: "accepted",
  Failed: "failed",
} as const;

export type LcmMessagePlanStatus =
  (typeof LcmMessagePlanStatuses)[keyof typeof LcmMessagePlanStatuses];

const VALID_MESSAGE_PLAN_STATUSES: readonly LcmMessagePlanStatus[] =
  Object.values(LcmMessagePlanStatuses);

export function isLcmMessagePlanStatus(
  value: string,
): value is LcmMessagePlanStatus {
  return (VALID_MESSAGE_PLAN_STATUSES as readonly string[]).includes(value);
}

/**
 * The default name stamped onto a candidate's content email/artifact at
 * generation time, `"{plan name} - Candidate {N}"`, used when the candidate has
 * no `name`. Every surface just reads the stored name/title. Renaming a plan
 * rewrites these defaults via the `renameMessagePlan` resolver; renaming a
 * candidate replaces it outright. Used by candidate generation (to stamp) and
 * the rename resolver (to recompute the old/new forms for its cascade). NOTE:
 * the backfill migration reproduces this format in SQL — keep them in sync.
 */
export function lcmCandidateContentName(
  planName: string,
  candidateNumber: number,
): string {
  return `${planName} - Candidate ${candidateNumber}`;
}

// A reference attached to a message plan for downstream generation.
export type MessagePlanReference =
  | { type: "image"; artifact_id: string; description: string }
  | { type: "url"; url: string; description: string }
  // A product resolved from the product catalog. `ad_artifacts_catalog_id` is
  // the `ad_artifacts_catalog.id` (NOT the overloaded `artifact_id` used by the
  // other variants, which point at `campaign_artifacts` / uploaded images) — it
  // is the exact catalog row the brief vetted, carried through so the content
  // editor uses that product without re-resolving. `title` is always present
  // (catalog title is required at import); `sku`/`description` are optional
  // because the SKU is a `user_tags` value and the catalog description is
  // nullable.
  | {
      type: "product_catalog_item";
      ad_artifacts_catalog_id: string;
      title: string;
      sku?: string;
      description?: string;
    };

// One alternative take on a message plan's message. `id` is a UUID assigned by
// the message-plan tools; generated `lcm_message_candidates` rows point back to
// it via `plan_candidate_id`.
export interface MessageCandidate {
  id: string;
  /**
   * Stable per-plan ordinal for display ("Candidate {N}"), assigned server-side
   * by the message-plan tools (never agent-authored). Always present on a stored
   * candidate: the tools stamp it on every write and a backfill stamped all
   * pre-existing blobs.
   */
  candidate_number: number;
  /** Name for the generated email or copy; `lcmCandidateContentName` applies when absent. */
  name?: string;
  /** Email template to reskin; required for email candidates. */
  template_id?: string;
  template_guidance?: string;
  message_angle: string;
  emotional_goal: string;
  existing_emails?: Array<{ artifact_id: string; description: string }>;
}

/**
 * Typed interface for the `plan` JSONB column on `lcm_message_plans`.
 * Importable by both backend and frontend.
 *
 * Blobs are written exclusively through the lcm-campaign-message-plan tools,
 * whose Zod schema (`MessagePlanSchema` in
 * `llm/skills/lcm-campaign-message-plan/tools.ts`) validates at write time and
 * is compile-time-bound to this type.
 */
export interface MessagePlan {
  /** Human-readable name for the message (e.g. 'Welcome Email'). */
  name: string;
  channel: LcmStudioChannel;
  recipient?: string;
  /**
   * Scannable one-liner of who the message is for, agent-authored alongside
   * `recipient` (which stays the full copywriter-voice description). Optional
   * because plans written before this field existed don't carry it — the UI
   * falls back to truncating `recipient`.
   */
  recipient_summary?: string;
  desired_action: string;
  guidance: string;
  candidates: MessageCandidate[];
  references?: MessagePlanReference[];
  /**
   * How email candidates should use their template (defaults to `adhere` when
   * absent — covers plans written before this field existed). Stamped from the
   * project's session context at plan creation; see lcm-campaign-message-plan
   * tools.
   */
  template_behavior?: TemplateBehavior;
  /**
   * Campaign-wide free-form direction on how to use the selected template(s),
   * authored by the user at project creation (Phoebe's "Free form" option).
   * Applies in addition to `template_behavior`. Stamped from session context at
   * plan creation and injected into the email candidate prompt. Distinct from a
   * candidate's own `template_guidance` (agent-authored, per-candidate).
   */
  template_guidance?: string;
}

type ConceptMessageVariantBase = {
  name: string;
  description: string;
  prompt: string;
};

export type ConceptMessageVariant =
  | (ConceptMessageVariantBase & { channel: "email"; templateId: string })
  | (ConceptMessageVariantBase & { channel: "push" })
  | (ConceptMessageVariantBase & { channel: "sms" });

/**
 * Typed interface for `concept_input_data` JSONB column on
 * `marketing_campaign_message_concepts`. Importable by both backend and frontend.
 */
export interface ConceptInputData {
  conceptId: string;
  conceptName: string;
  audienceStrategy: {
    description: string;
    segmentationCriteria: string[];
  };
  messagingStrategy: {
    coreMessage: string;
    toneAndStyle: string;
    keyThemes: string[];
  };
  messageVariants: Array<ConceptMessageVariant>;
  additionalPrompt?: string;

  /** History of all message generation events (initial + follow-ups) */
  messageGenerations?: Array<{
    messageVariants: Array<ConceptMessageVariant>;
    additionalPrompt?: string;
    sessionId: string;
  }>;
}
